import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowDown } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { SkeletonLoader } from './shared/SkeletonLoader';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';

export function StoreFunnel({ funnelData, isLoading, isMarketer }) {
  if (isMarketer) return null;
  if (isLoading) return <SkeletonLoader variant="chart" />;

  const steps = funnelData?.steps || [];
  const summary = funnelData?.summary || {};

  if (steps.length === 0) return null;

  const maxValue = Math.max(...steps.map(s => s.value || 0), 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader title="Store Funnel" icon={ShoppingBag} />
      <div className="glass-card rounded-2xl p-5">
        {/* Funnel steps - horizontal layout on desktop */}
        <div className="flex flex-col lg:flex-row items-stretch gap-2 lg:gap-0 mb-6">
          {steps.map((step, i) => {
            const pct = maxValue > 0 ? (step.value / maxValue) * 100 : 0;
            return (
              <React.Fragment key={i}>
                {/* Step card */}
                <div className="flex-1 relative">
                  <div className="glass-card rounded-xl p-4 text-center h-full border border-gray-100 hover:border-blue-200 transition-colors">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">{step.name}</p>
                    <p className="text-xl font-bold text-gray-900">{formatStat(step.value)}</p>
                    {step.percentage != null && (
                      <p className="text-[10px] text-gray-500 mt-1">{step.percentage}%</p>
                    )}
                    {/* Mini bar */}
                    <div className="h-1.5 rounded-full bg-gray-100 mt-2 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Dropoff arrow between steps */}
                {i < steps.length - 1 && (
                  <div className="flex items-center justify-center lg:w-10 py-1 lg:py-0 shrink-0">
                    <div className="flex flex-col items-center">
                      <ArrowDown size={14} className="text-gray-300 lg:rotate-[-90deg]" />
                      {step.dropoff > 0 && (
                        <span className="text-[9px] font-semibold text-red-500">-{step.dropoff}%</span>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Summary row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Overall CR</p>
            <p className="text-lg font-bold text-gray-900">{summary.conversionRate || 0}%</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Drop-off</p>
            <p className="text-lg font-bold text-red-500">{summary.dropoffRate || 0}%</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Revenue</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrencyCompact(summary.revenue || 0)}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">AOV</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrencyCompact(summary.avgOrderValue || 0)}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
